// Lesson progress (Phase 3, plan §6) on notebook reader pages.
//
// Members are the durable record: page_completions rows, written server-side
// (practice attempt hook + the mark-done endpoint). Guests mirror completions
// into localStorage; the mirror is merged into the DB on the first reader
// visit after sign-in — that merge is the sign-up incentive. The localStorage
// marks are also painted for members so a fresh completion shows before the
// next server render.

const DONE_KEY = 'ab_pages_done'     // { pageId: iso timestamp }
const SYNCED_KEY = 'ab_pages_synced' // [pageId, ...] already merged into the DB

function readJSON (key, fallback) {
    try {
        const raw = localStorage.getItem(key)
        return raw ? JSON.parse(raw) : fallback
    } catch (e) {
        return fallback
    }
}

function writeJSON (key, value) {
    try { localStorage.setItem(key, JSON.stringify(value)) } catch (e) { /* storage blocked */ }
}

function localDone () {
    const done = readJSON(DONE_KEY, {})
    return (done && typeof done === 'object') ? done : {}
}

function isMember () {
    const root = document.querySelector('[data-nb-member]')
    return root && root.dataset.nbMember === '1'
}

function csrfToken () {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : null
}

function gaEvent (name, params) {
    if (typeof window.gtag === 'function') window.gtag('event', name, params)
}

function postCompletions (pageIds) {
    const token = csrfToken()
    return fetch('/api/v1/page_completions', {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...(token ? { 'X-CSRF-Token': token } : {})
        },
        body: JSON.stringify({ page_ids: pageIds })
    })
}

function paintDone (pageId) {
    document.querySelectorAll(`.nb-nav-link[data-page-id="${pageId}"]`)
        .forEach(el => el.classList.add('is-done'))
    document.querySelectorAll(`.nb-mark-done[data-page-id="${pageId}"]`).forEach(button => {
        button.classList.add('is-done')
        button.disabled = true
        const label = button.querySelector('.nb-done-label')
        if (label) label.textContent = 'Lesson completed'
    })
}

// Mark a page complete: localStorage mirror + immediate UI. `post` sends the
// mark to the server (mark-done button for members); practice attempts skip it
// because the attempt capture already records the completion server-side.
export function markPageDone (pageId, { post = false } = {}) {
    if (!pageId) return
    pageId = String(pageId)

    const done = localDone()
    if (!done[pageId]) {
        done[pageId] = new Date().toISOString()
        writeJSON(DONE_KEY, done)
    }
    paintDone(pageId)

    if (post && isMember()) {
        postCompletions([Number(pageId)])
            .then(response => {
                if (!response.ok) return
                const synced = readJSON(SYNCED_KEY, [])
                if (!synced.includes(Number(pageId))) synced.push(Number(pageId))
                writeJSON(SYNCED_KEY, synced)
            })
            .catch(() => {}) // fire-and-forget: the localStorage mark survives
    }
}

// Merge the guest-era localStorage mirror into the DB once signed in.
function mergeMirror () {
    if (!isMember()) return

    const synced = readJSON(SYNCED_KEY, [])
    const unsynced = Object.keys(localDone()).map(Number)
        .filter(id => id > 0 && !synced.includes(id))
    if (!unsynced.length) return

    postCompletions(unsynced)
        .then(response => (response.ok ? response.json() : null))
        .then(body => {
            if (!body || !body.success) return
            writeJSON(SYNCED_KEY, synced.concat(body.page_ids || unsynced))
            gaEvent('lesson_progress_merged', { pages: unsynced.length })
        })
        .catch(() => {})
}

// Guest "continue where you left off": the member card is server-rendered;
// here we derive the same thing from painted checkmarks (server ∪ localStorage)
// and the notebook-root page list order.
function buildGuestContinue () {
    const slot = document.getElementById('nb-continue-slot')
    if (!slot || document.querySelector('.nb-continue')) return

    const links = Array.from(document.querySelectorAll('#page-content .nb-nav-link[data-page-id]'))
    if (!links.length) return

    const lastDone = links.reduce((last, link, index) => (link.classList.contains('is-done') ? index : last), -1)
    if (lastDone === -1) return
    const target = links.slice(lastDone + 1).find(link => !link.classList.contains('is-done')) ||
        links.find(link => !link.classList.contains('is-done'))
    if (!target) return

    const card = document.createElement('a')
    card.className = 'nb-continue'
    card.href = target.getAttribute('href')
    card.dataset.gaEvent = 'lesson_continue_click'
    card.dataset.gaParams = JSON.stringify({ page_id: Number(target.dataset.pageId) || null })

    const kicker = document.createElement('span')
    kicker.className = 'nb-continue-kicker'
    kicker.textContent = 'Continue where you left off'
    const title = document.createElement('span')
    title.className = 'nb-continue-title'
    title.textContent = `${target.textContent.trim()} →`
    card.append(kicker, title)
    slot.appendChild(card)

    if (!isMember()) {
        const nudge = document.createElement('p')
        nudge.className = 'nb-continue-nudge'
        nudge.innerHTML = '<a href="/users/sign_in">Sign in</a> to keep your progress across devices.'
        slot.appendChild(nudge)
    }
}

document.addEventListener('click', (event) => {
    const button = event.target.closest('.nb-mark-done')
    if (button && !button.disabled) {
        const pageId = button.dataset.pageId
        gaEvent('lesson_mark_done', { page_id: Number(pageId) || null })
        markPageDone(pageId, { post: true })
        return
    }

    const card = event.target.closest('.nb-continue')
    if (card) {
        let params = {}
        try { params = JSON.parse(card.dataset.gaParams || '{}') } catch (e) { /* noop */ }
        gaEvent(card.dataset.gaEvent || 'lesson_continue_click', params)
    }
})

function init () {
    Object.keys(localDone()).forEach(paintDone)
    mergeMirror()
    buildGuestContinue()
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
} else {
    init()
}
